import { Eye, EyeOff } from "lucide-react";
import { memo } from "react";
import { formatCurrency } from "@/config/locale";
import { t } from "./wallet-i18n";

interface BalanceCardProps {
  /** Available balance (can withdraw). */
  balance: number;
  /** Pending balance (not yet available). Optional; when provided and > 0, shown below available. */
  pending?: number;
  /** Reserved balance (e.g. Stripe connect_reserved). Optional; when provided and > 0, shown as "Em reserva". */
  reserved?: number;
  /** Escrow balance (unreleased; "Em garantia"). Optional; when provided and > 0, shown below reserved. */
  escrow?: number;
  showBalance: boolean;
  onToggleBalance: () => void;
  onWithdraw: () => void;
  onAddFunds: () => void;
}

export const BalanceCard = memo(function BalanceCard({
  balance,
  pending = 0,
  reserved = 0,
  escrow = 0,
  showBalance,
  onToggleBalance,
  onWithdraw,
  onAddFunds,
}: BalanceCardProps) {
  return (
    <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 shadow-lg m-4">
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <p className="text-white/80 text-sm mb-2">{t("availableBalance")}</p>
          <div className="flex items-center gap-3">
            <h2 className="text-3xl font-bold text-white">
              {showBalance ? formatCurrency(balance) : "R$ ••••••"}
            </h2>
            <button
              onClick={onToggleBalance}
              className="text-white/80 hover:text-white transition-colors"
              aria-label={showBalance ? t("hideBalance") : t("showBalance")}
            >
              {showBalance ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          {showBalance && pending > 0 && (
            <p className="text-white/70 text-sm mt-2">{t("pending")}: {formatCurrency(pending)}</p>
          )}
          {showBalance && reserved > 0 && (
            <p className="text-white/70 text-sm mt-1">{t("inReserve")}: {formatCurrency(reserved)}</p>
          )}
          {showBalance && (
            <p className="text-white/70 text-sm mt-1">{t("inEscrow")}: {formatCurrency(escrow)}</p>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onWithdraw}
          className="flex-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white py-3 rounded-xl font-medium transition-colors"
        >
          {t("withdraw")}
        </button>
        <button
          onClick={onAddFunds}
          className="flex-1 bg-white hover:bg-white/90 text-primary py-3 rounded-xl font-medium transition-colors"
        >
          {t("addFunds")}
        </button>
      </div>
    </div>
  );
});